import { joinDefinedStrings } from 'ComponentLibrary/src/common/strings'
import type { Address } from 'ComponentLibrary/src/common/address'
import type { UUID } from 'ComponentLibrary/src/common/uuid'

/** A persisted platform account with the identifiers and contact fields guaranteed by the user API. */
export type User = {
    role?: 'appraiser' | 'customer'
    uuid: UUID
    id: number
    salutation?: string
    job_title?: string
    first_name?: string
    last_name?: string
    email: string
    organization_name: string
    profile_image_url?: string
    profile_thumbnail_image_url?: string
    pinned_threads?: string[]
    isCurrentUser?: boolean
    is_platform_user?: boolean
    is_active?: boolean
}

type UserDisplayFields =
    | 'role'
    | 'uuid'
    | 'salutation'
    | 'job_title'
    | 'first_name'
    | 'last_name'
    | 'email'
    | 'organization_name'
    | 'profile_image_url'
    | 'profile_thumbnail_image_url'
    | 'isCurrentUser'
    | 'is_active'

/**
 * Partial user-like data accepted by presentation components. It may describe an account, external participant, or free-text
 * person; use UserPresentation when the provenance affects rendering.
 */
export type UserDisplayData = Partial<Pick<User, UserDisplayFields>> &
    Partial<Address> & {
        id?: User['id'] | UUID
        phone_number?: string
        certification?: string
        note?: string
    }

export type AppraiserAssignment = {
    main: string
    secondary?: string
    tertiary?: string
}

export type AppraiserUser = User & {
    role: 'appraiser'
    certification?: string
}

export type CustomerUser = User & {
    role: 'customer'
    team_uuid?: string
    customer_uuid: string
}

export type ProfileEntity = AppraiserUser | CustomerUser | Mandator | Contact

export function isAppraiserUser(value: ProfileEntity): value is AppraiserUser {
    return 'role' in value && value.role === 'appraiser'
}

export function isCustomerUser(value: ProfileEntity): value is CustomerUser {
    return 'role' in value && value.role === 'customer'
}

export type Team = {
    uuid: string
    name: string
}

export type Mandator = {
    name: string
    street?: string
    house_number?: string
    zip?: string
    city?: string
    country?: string
}

export type Contact = {
    salutation?: string
    name?: string
    job_title?: string
    company?: string
    street?: string
    house_number?: string
    zip?: string
    city?: string
    country?: string
    email?: string
    phone_number?: string
    note?: string
}

export type TaskAssignee = {
    id: number
    email: string
    is_active?: boolean
    organization_name?: string
    first_name?: string
    last_name?: string
}

type UserNameProps = Partial<{ first_name?: string; last_name?: string; name?: string }>

export function assembleClientName(client?: UserNameProps) {
    if (client?.first_name || client?.last_name) {
        return `${client.first_name || ''}${client.first_name && client.last_name ? ' ' : ''}${client.last_name || ''}`
    }

    if (client?.name) {
        return client.name
    }

    return undefined
}

export function assembleNameWithEmailFallback(
    user: UserNameProps & { email?: string; is_active?: boolean },
    options: { suffix?: string } = {}
) {
    const name = assembleClientName(user) || (user.is_active !== false && user.email) || ''
    return options.suffix ? `${name}${options.suffix}` : name
}

export function assembleUserSearchText(user: UserNameProps & { email?: string }) {
    return joinDefinedStrings([assembleClientName(user), user.email])
}
